import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { ApiTokenCreatedModal } from "@/pages/settings/api-tokens/widgets/ApiTokenCreatedModal.tsx";
import { useApiTokenGenerate } from "@/queries/settings/use-api-users.query.ts";
import { ApiApiUser } from "@/types/api-user.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Select,
  SelectItem,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const LIFETIME_UNLIMITED = "unlimited";
const LIFETIME_CUSTOM = "custom";
const UNLIMITED_DAYS = 3650;

export interface ApiTokenGenerateModalProps extends ModalProps {
  apiUser: ApiApiUser;
}

export function ApiTokenGenerateModal({ apiUser, ...props }: ApiTokenGenerateModalProps) {
  const { openModal, close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const lifetimeOptions = useMemo<SelectItem[]>(
    () => [
      { id: "30", title: t("plurals:plural_day", { count: 30 }) },
      { id: "90", title: t("plurals:plural_day", { count: 90 }) },
      { id: "180", title: t("plurals:plural_day", { count: 180 }) },
      { id: "365", title: t("plurals:plural_day", { count: 365 }) },
      { id: LIFETIME_UNLIMITED, title: t("core:api_token_lifetime_unlimited") },
      { id: LIFETIME_CUSTOM, title: t("core:api_token_lifetime_custom") },
    ],
    [t]
  );

  const requiredMessage = t("errors:field_required");
  const apiTokenSchema = z
    .object({
      name: z.string().min(1, requiredMessage),
      lifetime: z.string().min(1, requiredMessage),
      customDays: z.preprocess(
        (value) =>
          value === undefined || (typeof value === "string" && value.trim() === "")
            ? undefined
            : Number(value),
        z
          .number({ error: requiredMessage })
          .int(requiredMessage)
          .positive(requiredMessage)
          .optional()
      ),
    })
    .superRefine((data, context) => {
      if (data.lifetime === LIFETIME_CUSTOM && data.customDays === undefined) {
        context.addIssue({
          code: "custom",
          path: ["customDays"],
          message: requiredMessage,
        });
      }
    });

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(apiTokenSchema),
    defaultValues: { name: apiUser.name, lifetime: "90" },
  });

  const tokenGenerate = useApiTokenGenerate(control);

  const lifetime = watch("lifetime");

  const onSubmit = handleSubmit(async (data) => {
    const expiresInDays =
      data.lifetime === LIFETIME_UNLIMITED
        ? UNLIMITED_DAYS
        : data.lifetime === LIFETIME_CUSTOM
          ? (data.customDays as number)
          : Number(data.lifetime);

    try {
      const token = await tokenGenerate.mutateAsync({
        apiUserId: apiUser.id,
        payload: { name: data.name, expires_in_days: expiresInDays },
      });
      close(id);
      openModal(ApiTokenCreatedModal, { token });
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("core:api_token_generate_title")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={tokenGenerate.isPending}
            onClick={onSubmit}
          >
            {t("core:api_token_create")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Input
            required={true}
            label={t("core:name")}
            autoFocus={true}
            type="text"
            {...register("name")}
            error={errors.name?.message}
          />
          <Controller
            control={control}
            name="lifetime"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("core:api_token_lifetime")}
                emptyTitle={""}
                options={lifetimeOptions}
                value={value}
                onChange={(newValue) => onChange(newValue as string)}
                error={errors.lifetime?.message}
              />
            )}
          />
          {lifetime === LIFETIME_CUSTOM && (
            <Input
              required={true}
              label={t("core:api_token_lifetime_days")}
              type="number"
              min={1}
              {...register("customDays")}
              error={errors.customDays?.message}
            />
          )}
        </Stack>
      </form>
    </Modal>
  );
}
